import { IconButton, Stack, Tooltip } from "@mui/material";
import React, { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { redoFieldChange, undoFieldChange } from "redux/appSlice";
import editorTokens from "../SignRequestEditor.tokens";
import { RedoIcon, UndoIcon } from "./EditorIcons";

type Props = {
  size?: "small" | "medium";
  direction?: "row" | "column";
};

const { canvas, palette } = editorTokens;

/** Ghost square shared by both buttons — matches the editor toolbar chrome. */
const buttonSx = {
  width: canvas.actionSize,
  height: canvas.actionSize,
  borderRadius: canvas.actionRadius,
  color: canvas.toggleColor,
  "&:hover": { backgroundColor: palette.surface },
  "&.Mui-disabled": { color: palette.borderMain },
};

const UndoRedoButtons = ({ size = "small", direction = "row" }: Props) => {
  const dispatch = useAppDispatch();
  const selectedFile = useAppSelector(
    (state: RootState) => state.app.selectedFile,
  );
  const docId =
    selectedFile?.unsignedDoc || selectedFile?.unsignedPdf || "";
  const docHistory = useAppSelector(
    (state: RootState) => state.app.historyByDoc?.[docId],
  );

  const canUndo = (docHistory?.past?.length || 0) > 0;
  const canRedo = (docHistory?.future?.length || 0) > 0;

  const handleUndo = useCallback(() => {
    if (!docId) return;
    dispatch(undoFieldChange({ docId }));
  }, [dispatch, docId]);

  const handleRedo = useCallback(() => {
    if (!docId) return;
    dispatch(redoFieldChange({ docId }));
  }, [dispatch, docId]);

  return (
    <Stack
      direction={direction}
      spacing={1}
      alignItems="center"
      justifyContent="center"
    >
      <Tooltip title="Undo (Ctrl+Z)" arrow describeChild>
        <span>
          <IconButton
            size={size}
            onClick={handleUndo}
            disabled={!canUndo}
            aria-label="Undo last action"
            sx={buttonSx}
          >
            <UndoIcon size={canvas.actionIconSize} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Redo (Ctrl+Shift+Z)" arrow describeChild>
        <span>
          <IconButton
            size={size}
            onClick={handleRedo}
            disabled={!canRedo}
            aria-label="Redo last undone action"
            sx={buttonSx}
          >
            <RedoIcon size={canvas.actionIconSize} />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
};

export default UndoRedoButtons;
